import { useAuth } from "@/lib/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, UserPlus, ChevronDown, ChevronRight, Building2, Trash2, ShieldCheck, Users, Crown } from "lucide-react";
import { formatDisplayName } from "@/lib/names";

const PLAN_TIERS = [
  { key: "tier_1", label: "Tier 1 — $19/mo",  hint: "0–50 directory profiles" },
  { key: "tier_2", label: "Tier 2 — $29/mo",  hint: "51–250 directory profiles" },
  { key: "tier_3", label: "Tier 3 — $39/mo",  hint: "251–1,000 directory profiles" },
  { key: "tier_4", label: "Tier 4 — $59/mo",  hint: "1,001–2,000 directory profiles" },
];
const TIER_LIMITS = {
  tier_1: { members: 50 },
  tier_2: { members: 250 },
  tier_3: { members: 1000 },
  tier_4: { members: 2000 },
};
const ROLE_META = {
  org_admin: { label: "Organization Admin", icon: Crown, tint: "var(--ember)" },
  coordinator: { label: "Coordinator", icon: ShieldCheck, tint: "var(--terracotta)" },
  teacher: { label: "Teacher", icon: Users, tint: "var(--muted)" },
};

const emptyOrg = {
  name: "", city: "", contact_email: "",
  plan_tier: "tier_1",
  tools_enabled: ["tracker", "attendance", "prayer", "kiosk"],
  free_trial: false,
  trial_days: 30,
};

const TOOL_OPTIONS = [
  { key: "tracker",    label: "Bible Study Tracker", required: true },
  { key: "attendance", label: "Attendance" },
  { key: "prayer",     label: "Prayer Log" },
  { key: "kiosk",      label: "Kiosk" },
];
const emptyOrgAdmin = { email: "", name: "", password: "" };

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>{label}</Label>
      {children}
    </div>
  );
}

function OrgModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    if (!initial) return emptyOrg;
    return {
      ...initial,
      tools_enabled: Array.isArray(initial.tools_enabled) && initial.tools_enabled.length
        ? initial.tools_enabled
        : ["tracker", "attendance", "prayer", "kiosk"],
    };
  });
  const [admin1, setAdmin1] = useState(emptyOrgAdmin);
  const [admin2, setAdmin2] = useState(emptyOrgAdmin);
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial?.id;

  const validateAdmin = (a, label) => {
    if (!a.email && !a.name && !a.password) return null; // empty slot: skip
    if (!a.email || !a.name || !a.password) return `${label}: name, email, and password are all required.`;
    if (a.password.length < 8) return `${label}: password must be at least 8 characters.`;
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required.");
    if (!form.tools_enabled || form.tools_enabled.length === 0) {
      return toast.error("Enable at least one tool for this church.");
    }
    if (!isEdit) {
      const err1 = validateAdmin(admin1, "First org admin");
      if (err1) return toast.error(err1);
      const err2 = validateAdmin(admin2, "Second org admin");
      if (err2) return toast.error(err2);
    }
    setBusy(true);
    try {
      const payload = { ...form };
      delete payload.max_coordinators;
      delete payload.max_teachers;
      let orgId = initial?.id;
      if (isEdit) {
        await api.put(`/admin/orgs/${initial.id}`, payload);
      } else {
        const r = await api.post("/admin/orgs", payload);
        orgId = r.data.id;
        // Provision org admins if provided
        for (const [i, a] of [[1, admin1], [2, admin2]]) {
          if (a.email && a.name && a.password) {
            try {
              await api.post(`/admin/orgs/${orgId}/org-admins`, {
                email: a.email.trim(),
                name: a.name.trim(),
                password: a.password,
              });
            } catch (err) {
              toast.error(`Org created but admin ${i} failed: ${err?.response?.data?.detail || "unknown error"}`);
            }
          }
        }
      }
      toast.success(isEdit ? "Organization updated." : "Organization created.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="org-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>
            {isEdit ? "Edit organization" : "New organization"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <Field label="Name *">
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="org-name" />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="City / Area">
              <Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="org-city" />
            </Field>
            <Field label="Contact email">
              <Input type="email" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="org-contact-email" />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Plan tier (member size)">
              <select value={form.plan_tier} onChange={(e) => setForm({ ...form, plan_tier: e.target.value })} className="w-full px-3 py-2 text-sm border" style={{ borderColor: "var(--border-strong)" }} data-testid="org-plan-tier">
                {PLAN_TIERS.map((t) => <option key={t.key} value={t.key}>{t.label} — {t.hint}</option>)}
              </select>
            </Field>
            <Field label="Directory cap (derived)">
              <div className="px-3 py-2 text-sm" style={{ background: "var(--bg-base)", borderLeft: "3px solid var(--ember)", color: "var(--ink-2)" }} data-testid="org-seats-derived">
                {TIER_LIMITS[form.plan_tier]?.members || 0} members. Coordinators, teachers, org admins, and section leaders are unlimited on every tier.
              </div>
            </Field>
          </div>

          <Field label="Enabled tools *">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="org-tools-group">
              {TOOL_OPTIONS.map((tool) => {
                const on = tool.required || (form.tools_enabled || []).includes(tool.key);
                return (
                  <label
                    key={tool.key}
                    className={`flex items-center gap-2 px-3 py-2 border ${tool.required ? "cursor-default" : "cursor-pointer"}`}
                    style={{
                      borderColor: tool.required ? "var(--gold)" : on ? "var(--ember)" : "var(--border-subtle)",
                      background: on ? "var(--bg-base)" : "transparent",
                      opacity: tool.required ? 0.95 : 1,
                    }}
                    data-testid={`org-tool-${tool.key}`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4"
                      style={{ accentColor: tool.required ? "var(--gold)" : "var(--ember)" }}
                      checked={on}
                      disabled={tool.required}
                      onChange={() => {
                        if (tool.required) return;
                        setForm({
                          ...form,
                          tools_enabled: on
                            ? (form.tools_enabled || []).filter((t) => t !== tool.key)
                            : [...(form.tools_enabled || []), tool.key],
                        });
                      }}
                      data-testid={`org-tool-checkbox-${tool.key}`}
                    />
                    <span className="text-sm flex-1" style={{ color: "var(--ink)" }}>{tool.label}</span>
                    {tool.required && (
                      <span
                        className="text-[0.55rem] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full"
                        style={{ background: "var(--gold)", color: "var(--bg-base)" }}
                        data-testid={`org-tool-required-badge-${tool.key}`}
                      >
                        Required
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="text-[0.65rem] mt-1" style={{ color: "var(--muted)" }}>
              Bible Study Tracker is the required base for every church. Add-ons layer on top at prices set by the tier.
            </div>
          </Field>
          {isEdit && (
            <Field label="Active">
              <select value={form.active === false ? "no" : "yes"} onChange={(e) => setForm({ ...form, active: e.target.value === "yes" })} className="w-full px-3 py-2 text-sm border" style={{ borderColor: "var(--border-strong)" }} data-testid="org-active">
                <option value="yes">Active</option>
                <option value="no">Inactive (hidden from coordinators)</option>
              </select>
            </Field>
          )}

          {!isEdit && (
            <Field label="Free trial">
              <div
                className="flex items-start gap-3 p-3 border"
                style={{
                  borderColor: form.free_trial ? "var(--gold)" : "var(--border-subtle)",
                  background: form.free_trial ? "var(--bg-base)" : "transparent",
                }}
                data-testid="org-free-trial-wrap"
              >
                <input
                  id="org-free-trial"
                  type="checkbox"
                  className="h-4 w-4 mt-1"
                  style={{ accentColor: "var(--gold)" }}
                  checked={!!form.free_trial}
                  onChange={(e) => setForm({ ...form, free_trial: e.target.checked })}
                  data-testid="org-free-trial-checkbox"
                />
                <div className="flex-1">
                  <label htmlFor="org-free-trial" className="text-sm cursor-pointer" style={{ color: "var(--ink)" }}>
                    Start this church on a free trial
                  </label>
                  <div className="text-[0.65rem] mt-0.5" style={{ color: "var(--muted)" }}>
                    They'll have full access to every enabled tool for the trial window — no card required.
                  </div>
                  {form.free_trial && (
                    <div className="mt-2 flex items-center gap-2">
                      <Label className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Trial length (days)</Label>
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        value={form.trial_days ?? 30}
                        onChange={(e) => setForm({ ...form, trial_days: Math.max(1, Number(e.target.value) || 30) })}
                        className="w-24 h-8 text-sm"
                        data-testid="org-trial-days"
                      />
                    </div>
                  )}
                </div>
              </div>
            </Field>
          )}

          {isEdit && (
            <Field label="Billing mode">
              <div className="space-y-2" data-testid="org-billing-mode-wrap">
                {[
                  { key: "free",    status: "grandfathered",   label: "Free (comped)",      desc: "No charges. Full access to every enabled tool." },
                  { key: "trial",   status: "trialing",         label: "Free trial",         desc: "Full access until the trial ends, then flips to \"Requires payment\"." },
                  { key: "paid",    status: "pending_payment", label: "Requires payment",   desc: "Church must complete Stripe checkout to use any tool." },
                ].map((opt) => {
                  const isActiveNow = form.billing_status === "active" || form.billing_status === "past_due";
                  const checked = form.billing_status === opt.status;
                  return (
                    <label
                      key={opt.key}
                      className={`flex items-start gap-3 p-3 border transition-colors ${isActiveNow ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                      style={{
                        borderColor: checked ? "var(--gold)" : "var(--border-subtle)",
                        background: checked ? "var(--bg-base)" : "transparent",
                      }}
                      data-testid={`org-billing-mode-${opt.key}`}
                    >
                      <input
                        type="radio"
                        name="org-billing-mode"
                        className="h-4 w-4 mt-1"
                        style={{ accentColor: "var(--gold)" }}
                        checked={checked}
                        disabled={isActiveNow}
                        onChange={() => setForm({
                          ...form,
                          billing_status: opt.status,
                          free_trial: opt.key === "trial" ? true : (opt.key === "free" ? false : false),
                          trial_days: opt.key === "trial" ? (form.trial_days || 30) : form.trial_days,
                        })}
                        data-testid={`org-billing-mode-radio-${opt.key}`}
                      />
                      <div className="flex-1">
                        <div className="text-sm" style={{ color: "var(--ink)" }}>{opt.label}</div>
                        <div className="text-[0.65rem] mt-0.5" style={{ color: "var(--muted)" }}>{opt.desc}</div>
                        {opt.key === "trial" && checked && (
                          <div className="mt-2 flex items-center gap-2">
                            <Label className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Trial length (days)</Label>
                            <Input
                              type="number"
                              min={1}
                              max={365}
                              value={form.trial_days ?? 30}
                              onChange={(e) => setForm({ ...form, trial_days: Math.max(1, Number(e.target.value) || 30) })}
                              className="w-24 h-8 text-sm"
                              data-testid="org-edit-trial-days"
                            />
                            {initial?.trial_ends_at && (
                              <span className="text-[0.65rem]" style={{ color: "var(--muted)" }}>
                                · Current trial ends {new Date(initial.trial_ends_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
                {(form.billing_status === "active" || form.billing_status === "past_due") && (
                  <div className="text-[0.65rem] italic p-2" style={{ color: "var(--muted)", background: "var(--bg-base)" }}>
                    This church has an {form.billing_status === "active" ? "active" : "overdue"} Stripe subscription. Cancel or refund it from the Stripe dashboard first, then flip the billing mode here.
                  </div>
                )}
              </div>
            </Field>
          )}

          {!isEdit && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--border-subtle)" }}>
              <div className="flex items-center gap-2 mb-3">
                <Crown size={16} style={{ color: "var(--ember)" }} />
                <div>
                  <div className="text-sm font-medium" style={{ color: "var(--ink)" }}>Assign organization admin(s)</div>
                  <div className="text-xs" style={{ color: "var(--muted)" }}>Every org needs at least 1 (and up to 2) org admins. They can create teachers and grant coordinator privileges.</div>
                </div>
              </div>
              {[[1, admin1, setAdmin1, "First"], [2, admin2, setAdmin2, "Second"]].map(([n, a, set, lab]) => (
                <div key={n} className="p-3 mb-2" style={{ background: "var(--bg-base)", borderLeft: "3px solid var(--ember)" }} data-testid={`org-admin-slot-${n}`}>
                  <div className="text-[0.6rem] uppercase tracking-[0.18em] mb-2" style={{ color: "var(--muted)" }}>{lab} org admin {n === 2 && "(optional)"}</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Input placeholder="Full name" value={a.name} onChange={(e) => set({ ...a, name: e.target.value })} data-testid={`org-admin-${n}-name`} />
                    <Input placeholder="Email" type="email" value={a.email} onChange={(e) => set({ ...a, email: e.target.value })} data-testid={`org-admin-${n}-email`} />
                    <Input placeholder="Password (≥8 chars)" type="text" value={a.password} onChange={(e) => set({ ...a, password: e.target.value })} data-testid={`org-admin-${n}-password`} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter className="mt-4">
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="org-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="org-submit">
              {busy ? "Saving…" : (isEdit ? "Save changes" : "Create organization")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddOrgAdminModal({ org, onClose, onSaved }) {
  const [form, setForm] = useState(emptyOrgAdmin);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required.");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    setBusy(true);
    try {
      await api.post(`/admin/orgs/${org.id}/org-admins`, form);
      toast.success(`${form.name} is now an Org Admin of ${org.name}.`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add org admin.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="add-org-admin-modal" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl" style={{ color: "var(--ink)" }}>Add organization admin</DialogTitle>
          <p className="text-sm" style={{ color: "var(--muted)" }}>
            Adding to <strong style={{ color: "var(--ink)" }}>{org.name}</strong>. Max 2 org admins per org.
          </p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <Field label="Full name *">
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-org-admin-name" />
          </Field>
          <Field label="Email *">
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-org-admin-email" />
          </Field>
          <Field label="Temporary password (min 8 chars)">
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-org-admin-password" placeholder="Share this with them; they can change it after login." />
          </Field>
          <DialogFooter className="mt-4">
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="new-org-admin-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="new-org-admin-submit">
              {busy ? "Adding…" : "Add org admin"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MemberRow({ member, onToggleCoord, onRevokeOrgAdmin, onPromoteOrgAdmin, onDeleteUser, onSetGender, isSystemAdmin }) {
  const meta = ROLE_META[member.role] || ROLE_META.teacher;
  const Icon = meta.icon;
  const [busy, setBusy] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [savingGender, setSavingGender] = useState(false);

  const toggle = async () => {
    if (member.is_org_admin) {
      toast.info("Coordinator access is implicit for Org Admins.");
      return;
    }
    if (!member.user_id) {
      toast.error("This teacher hasn't been provisioned a login yet.");
      return;
    }
    setBusy(true);
    try {
      await onToggleCoord(member.user_id, !member.is_coordinator);
    } finally {
      setBusy(false);
    }
  };

  const promote = async () => {
    if (!member.user_id) {
      toast.error("This teacher hasn't been provisioned a login yet.");
      return;
    }
    if (!window.confirm(`Promote ${member.name || "this user"} to Org Admin? They'll get full organization privileges.`)) return;
    setPromoting(true);
    try {
      await onPromoteOrgAdmin(member.user_id, member.name);
    } finally {
      setPromoting(false);
    }
  };

  const del = async () => {
    if (!member.user_id) {
      toast.error("No login account to delete.");
      return;
    }
    if (!window.confirm(`Permanently delete ${member.name || member.email || "this user"} (${member.email})?\n\nThis removes their login and teacher record. Use this to clean up ghost / duplicate accounts.`)) return;
    setDeleting(true);
    try {
      await onDeleteUser(member.user_id, member.name || member.email);
    } finally {
      setDeleting(false);
    }
  };

  const pickGender = async (g) => {
    if (!member.user_id) {
      toast.error("This teacher hasn't been provisioned a login yet.");
      return;
    }
    setSavingGender(true);
    try {
      await onSetGender(member.user_id, g);
    } finally {
      setSavingGender(false);
    }
  };

  // Compose "Bro. / Sis." into the displayed name when the gender is set.
  const displayed = formatDisplayName({ first_name: member.name || "", last_name: "", gender: member.gender }) || member.name || "(no name)";

  return (
    <div className="flex items-center justify-between gap-3 py-2 px-3" style={{ background: "var(--bg-base)" }} data-testid={`member-row-${member.user_id || member.teacher_id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <Icon size={16} style={{ color: meta.tint, flexShrink: 0 }} />
        <div className="min-w-0">
          <div className="font-medium text-sm flex items-center gap-2" style={{ color: "var(--ink)" }}>
            <span className="truncate">{displayed}</span>
            <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5" style={{ background: meta.tint, color: "#FFFFFF" }}>{meta.label}</span>
            {!member.has_login && <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5" style={{ background: "var(--muted)", color: "#FFFFFF" }}>No login</span>}
          </div>
          <div className="text-xs" style={{ color: "var(--muted)" }}>{member.email}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
        {member.has_login && (
          <div className="flex items-center gap-1" data-testid={`member-gender-group-${member.user_id}`} title="Set the title prefix used across the app">
            {[
              { key: "male", label: "Bro." },
              { key: "female", label: "Sis." },
              { key: null, label: "—" },
            ].map((opt) => {
              const active = (member.gender || null) === opt.key;
              return (
                <button
                  key={String(opt.key)}
                  onClick={() => pickGender(opt.key)}
                  disabled={savingGender}
                  className="px-2 py-0.5 text-[0.65rem] rounded-full border transition-colors"
                  style={{
                    borderColor: active ? "var(--ember)" : "var(--border-subtle)",
                    background: active ? "var(--ember)" : "transparent",
                    color: active ? "#FFFFFF" : "var(--ink-2)",
                    minWidth: opt.label === "—" ? 28 : 44,
                  }}
                  data-testid={`member-gender-${member.user_id}-${opt.key || "none"}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
        {member.has_login && !member.is_org_admin && (
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none" data-testid={`member-toggle-coord-${member.user_id}`}>
            <input
              type="checkbox"
              disabled={busy || !member.user_id}
              checked={!!member.is_coordinator}
              onChange={toggle}
              className="h-4 w-4"
              style={{ accentColor: "var(--terracotta)" }}
            />
            <span style={{ color: "var(--ink-2)" }}>Coordinator</span>
          </label>
        )}
        {member.has_login && !member.is_org_admin && (
          <button
            onClick={promote}
            disabled={promoting || !member.user_id}
            className="text-[0.65rem] uppercase tracking-[0.14em] px-2 py-1 border transition-colors"
            style={{
              borderColor: "var(--ember)",
              color: "var(--ember)",
              background: "transparent",
            }}
            title="Grant this user full Org Admin privileges."
            data-testid={`member-promote-org-admin-${member.user_id}`}
          >
            <Crown size={12} className="inline mr-1" style={{ marginTop: -2 }} />
            {promoting ? "Promoting…" : "Promote"}
          </button>
        )}
        {member.is_org_admin && (
          <button
            onClick={() => onRevokeOrgAdmin(member.user_id, member.name)}
            className="p-1.5"
            style={{ color: "var(--terracotta)" }}
            title="Revoke Org Admin"
            data-testid={`member-revoke-org-admin-${member.user_id}`}
          >
            <Trash2 size={14} />
          </button>
        )}
        {isSystemAdmin && member.user_id && (
          <button
            onClick={del}
            disabled={deleting}
            className="p-1.5"
            style={{ color: "var(--muted)" }}
            title="Delete user permanently (use for ghost / duplicate accounts)"
            data-testid={`member-delete-${member.user_id}`}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function UsagePanel({ usage, testid }) {
  const pctColor = (c, m) => {
    if (!m) return "var(--muted)";
    const p = c / m;
    if (p >= 1) return "var(--terracotta)";
    if (p >= 0.8) return "var(--gold)";
    return "var(--olive)";
  };
  const Bar = ({ label, current, max, tid }) => {
    const pct = max ? Math.min(100, (current / max) * 100) : 0;
    const color = pctColor(current, max);
    return (
      <div className="text-xs" data-testid={tid}>
        <div className="flex justify-between mb-1">
          <span style={{ color: "var(--ink-2)" }}>{label}</span>
          <span className="tabular-nums" style={{ color }}>{current} / {max}</span>
        </div>
        <div className="h-1 rounded-full overflow-hidden" style={{ background: "var(--surface-hover)" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: color }} />
        </div>
      </div>
    );
  };

  const att = usage.attendance || { tier_label: usage.tier_label, price_usd: usage.price_usd, member_range: usage.member_range, usage: usage.usage };
  const trk = usage.tracker;

  return (
    <div className="rounded-md overflow-hidden" style={{ border: "1px solid var(--border-subtle)" }} data-testid={testid}>
      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* ATTENDANCE PLAN */}
        <div className="p-4" style={{ background: "var(--bg-base)", borderRight: "1px solid var(--border-subtle)" }}>
          <div className="flex items-center justify-between mb-3">
            <div className="overline" style={{ fontSize: "0.6rem" }}>Attendance · {att.tier_label} <span style={{ color: "var(--muted)" }}>· ${att.price_usd}/mo · {att.member_range}</span></div>
            <span className="text-[0.55rem] uppercase tracking-wider" style={{ color: "var(--muted)" }}>Free while testing</span>
          </div>
          <div className="space-y-2">
            {att.usage?.members         && <Bar label="Members (directory)" current={att.usage.members.current}         max={att.usage.members.max}         tid={`${testid}-members`} />}
            {att.usage?.org_admins      && <Bar label="Org admins"          current={att.usage.org_admins.current}      max={att.usage.org_admins.max}      tid={`${testid}-org_admins`} />}
            {att.usage?.section_leaders && <Bar label="Section leaders"     current={att.usage.section_leaders.current} max={att.usage.section_leaders.max} tid={`${testid}-section_leaders`} />}
          </div>
        </div>

        {/* TRACKER PLAN */}
        <div className="p-4" style={{ background: "var(--bg-base)" }}>
          {trk ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="overline" style={{ fontSize: "0.6rem" }}>Bible-Study Tracker · {trk.tier_label} <span style={{ color: "var(--muted)" }}>· ${trk.price_usd}/mo</span></div>
                <span className="text-[0.55rem] uppercase tracking-wider" style={{ color: "var(--muted)" }}>Free while testing</span>
              </div>
              <div className="space-y-2">
                {trk.usage?.coordinators && <Bar label="Coordinators" current={trk.usage.coordinators.current} max={trk.usage.coordinators.max} tid={`${testid}-coordinators`} />}
                {trk.usage?.teachers     && <Bar label="Teachers"     current={trk.usage.teachers.current}     max={trk.usage.teachers.max}     tid={`${testid}-teachers`} />}
              </div>
            </>
          ) : (
            <p className="text-xs" style={{ color: "var(--muted)" }}>No tracker plan assigned.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function OrgRow({ org, onEdit, onChanged }) {
  const { user: currentUser } = useAuth();
  const isSystemAdmin = !!currentUser?.is_admin;
  const [expanded, setExpanded] = useState(false);
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addAdminOpen, setAddAdminOpen] = useState(false);
  const [usage, setUsage] = useState(null);

  const loadRoster = async () => {
    setLoading(true);
    try {
      const [r, u] = await Promise.all([
        api.get(`/admin/orgs/${org.id}/members`),
        api.get(`/admin/orgs/${org.id}/usage`),
      ]);
      setRoster(r.data);
      setUsage(u.data);
    } catch { toast.error("Couldn't load members."); }
    finally { setLoading(false); }
  };

  const toggle = () => {
    if (!expanded) loadRoster();
    setExpanded(!expanded);
  };

  const toggleCoord = async (userId, on) => {
    try {
      await api.put(`/admin/users/${userId}/toggle-coordinator`, { on });
      toast.success(on ? "Coordinator access granted." : "Coordinator access revoked.");
      loadRoster();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update role.");
    }
  };

  const revokeOrgAdmin = async (userId, name) => {
    if (!window.confirm(`Revoke Org Admin from ${name || "this user"}? They will remain a coordinator.`)) return;
    try {
      await api.delete(`/admin/org-admins/${userId}`);
      toast.success("Org Admin revoked.");
      loadRoster();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't revoke.");
    }
  };

  const promoteOrgAdmin = async (userId, name) => {
    try {
      await api.put(`/admin/users/${userId}/promote-org-admin`);
      toast.success(`${name || "User"} is now an Org Admin.`);
      loadRoster();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't promote.");
    }
  };

  const deleteUser = async (userId, label) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success(`Deleted ${label || "user"}.`);
      loadRoster();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete user.");
    }
  };

  const setGender = async (userId, gender) => {
    try {
      await api.put(`/admin/users/${userId}/gender`, { gender });
      toast.success(gender === "male" ? "Set as Bro." : gender === "female" ? "Set as Sis." : "Cleared.");
      loadRoster();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update.");
    }
  };

  const planLabel = PLAN_TIERS.find((t) => t.key === org.plan_tier)?.label || org.plan_tier;
  const orgAdmins = (roster?.members || []).filter((m) => m.role === "org_admin");
  const coordinators = (roster?.members || []).filter((m) => m.role === "coordinator");
  const teachers = (roster?.members || []).filter((m) => m.role === "teacher");
  const orgAdminMax = usage?.attendance?.usage?.org_admins?.max ?? usage?.usage?.org_admins?.max ?? org.max_org_admins ?? 1;
  const canAddOrgAdmin = orgAdmins.length < orgAdminMax;

  return (
    <div className="card-surface p-5" data-testid={`org-row-${org.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <button onClick={toggle} className="p-1 -ml-1 mt-0.5" style={{ color: "var(--muted)" }} data-testid={`org-toggle-${org.id}`}>
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Building2 size={16} style={{ color: "var(--ember)" }} />
            <h3 className="font-serif-display text-xl leading-tight" style={{ color: "var(--ink)" }}>{org.name}</h3>
            {!org.active && <span className="text-[0.55rem] uppercase tracking-wider px-1.5 py-0.5" style={{ background: "var(--muted)", color: "#FFFFFF" }}>Inactive</span>}
          </div>
          <div className="text-xs mt-1 flex flex-wrap gap-x-3 gap-y-1" style={{ color: "var(--muted)" }}>
            {org.city && <span>{org.city}</span>}
            {org.contact_email && <span>· {org.contact_email}</span>}
            <span className="w-full sm:w-auto">· Plan: <strong style={{ color: "var(--ink-2)" }}>{planLabel}</strong> <span style={{ color: "var(--muted)" }}>· up to {org.max_members ?? TIER_LIMITS[org.plan_tier]?.members ?? "—"} directory profiles</span></span>
            <span>· Tools: <strong style={{ color: "var(--ink-2)" }}>{(org.tools_enabled || []).length}</strong> enabled</span>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={onEdit} className="btn-ghost" data-testid={`org-edit-${org.id}`}><Pencil size={14} /> Edit</button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--border-subtle)" }} data-testid={`org-roster-${org.id}`}>
          {loading ? (
            <p className="text-sm" style={{ color: "var(--muted)" }}>Loading…</p>
          ) : roster ? (
            <div className="space-y-5">
              {usage && <UsagePanel usage={usage} testid={`org-usage-${org.id}`} />}
              {/* Org Admins */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <div className="overline flex items-center gap-2" style={{ fontSize: "0.6rem" }}>
                    <Crown size={12} style={{ color: "var(--ember)" }} /> Organization admins ({orgAdmins.length} / {orgAdminMax})
                  </div>
                  {canAddOrgAdmin && (
                    <button onClick={() => setAddAdminOpen(true)} className="btn-ghost text-xs" data-testid={`org-add-admin-${org.id}`}>
                      <UserPlus size={12} /> Add org admin
                    </button>
                  )}
                </div>
                {orgAdmins.length === 0 ? (
                  <p className="text-sm italic px-3 py-2" style={{ color: "var(--muted)", background: "var(--bg-base)" }}>
                    No org admins assigned. Add one so this church can manage its own team.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {orgAdmins.map((m) => (
                      <MemberRow key={m.user_id} member={m} onToggleCoord={toggleCoord} onRevokeOrgAdmin={revokeOrgAdmin} onPromoteOrgAdmin={promoteOrgAdmin} onDeleteUser={deleteUser} onSetGender={setGender} isSystemAdmin={isSystemAdmin} />
                    ))}
                  </div>
                )}
              </section>

              {/* Coordinators */}
              <section>
                <div className="overline mb-2 flex items-center gap-2" style={{ fontSize: "0.6rem" }}>
                  <ShieldCheck size={12} style={{ color: "var(--terracotta)" }} /> Coordinators ({coordinators.length})
                </div>
                {coordinators.length === 0 ? (
                  <p className="text-sm italic px-3 py-2" style={{ color: "var(--muted)", background: "var(--bg-base)" }}>
                    No coordinators yet. Toggle the coordinator switch on a teacher below to promote them.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {coordinators.map((m) => (
                      <MemberRow key={m.user_id} member={m} onToggleCoord={toggleCoord} onRevokeOrgAdmin={revokeOrgAdmin} onPromoteOrgAdmin={promoteOrgAdmin} onDeleteUser={deleteUser} onSetGender={setGender} isSystemAdmin={isSystemAdmin} />
                    ))}
                  </div>
                )}
              </section>

              {/* Teachers */}
              <section>
                <div className="overline mb-2 flex items-center gap-2" style={{ fontSize: "0.6rem" }}>
                  <Users size={12} style={{ color: "var(--muted)" }} /> Teachers ({teachers.length})
                </div>
                {teachers.length === 0 ? (
                  <p className="text-sm italic px-3 py-2" style={{ color: "var(--muted)", background: "var(--bg-base)" }}>
                    No teachers yet. Org admins add teachers from the CRM Teachers tab.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {teachers.map((m) => (
                      <MemberRow key={m.user_id || m.teacher_id} member={m} onToggleCoord={toggleCoord} onRevokeOrgAdmin={revokeOrgAdmin} onPromoteOrgAdmin={promoteOrgAdmin} onDeleteUser={deleteUser} onSetGender={setGender} isSystemAdmin={isSystemAdmin} />
                    ))}
                  </div>
                )}
              </section>
            </div>
          ) : null}
        </div>
      )}

      {addAdminOpen && (
        <AddOrgAdminModal
          org={org}
          onClose={() => setAddAdminOpen(false)}
          onSaved={() => { loadRoster(); onChanged?.(); }}
        />
      )}
    </div>
  );
}

export default function AdminOrganizations() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { org?: {...} } or {} for new

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/orgs");
      setOrgs(r.data);
    } catch { toast.error("Couldn't load organizations."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-organizations">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-serif-display text-2xl tracking-tight" style={{ color: "var(--ink)" }}>Organizations</h2>
          <p className="mt-1 text-sm" style={{ color: "var(--muted)" }}>
            Each church has its own org admins, coordinators, and teachers. Expand a row to see the full roster.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setModal({})} className="btn-primary" data-testid="admin-add-org-btn"><Plus size={14} /> New organization</button>
        </div>
      </div>

      {loading ? (
        <p style={{ color: "var(--muted)" }}>Loading…</p>
      ) : orgs.length === 0 ? (
        <div className="card-surface p-16 text-center">
          <Building2 size={32} style={{ color: "var(--muted)", margin: "0 auto" }} />
          <p className="mt-4 text-sm" style={{ color: "var(--muted)" }}>No organizations yet. Create your first one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orgs.map((o) => (
            <OrgRow key={o.id} org={o} onEdit={() => setModal({ org: o })} onChanged={load} />
          ))}
        </div>
      )}

      {modal && <OrgModal initial={modal.org} onClose={() => setModal(null)} onSaved={load} />}
    </div>
  );
}
